import { Icon, IconButton, isArrowKey, isBackspaceKey, } from "@smartsuite/react-ui/lib";
import { TextColorNames } from "@smartsuite/types";
import React, { useCallback, useMemo, useState } from "react";
import { isEnterKey, isEscKey } from "@smartsuite/react-ui/lib/helpers/keys";
import AiLoader from "./AiLoader";
import { AiActions } from "./AiActions";
import { filterCommands, groupCommands } from "./AiPopup.helpers";
import { useListNavigation, } from "../../../../core/hooks/useListNavigation";
import { AiPopupState, } from "../../types";
import { Text } from "@smartsuite/react-ui/lib/components/typography/Text/Text";
import { InlineResultBox } from "./InlineResultBox";
import { Textarea } from "@smartsuite/react-ui/lib/components/forms/Textarea/Textarea";
import { AiUsage } from "./AiUsage";
export const AiPopup = ({ onClose, onPrompt, loading, state, commands, groupLabels, onRunCommand, currentResult, error, results, navigateHistoryLeft, navigateHistoryRight, keyEvent = "onKeyDown", getAiUsageParams, popupRef, }) => {
    const [inputValue, setInputValue] = useState("");
    const inputRef = React.useRef(null);
    const usageParams = getAiUsageParams();
    // Filter commands based on input value and included states
    const filteredCommands = useMemo(() => {
        return filterCommands(commands, state, inputValue);
    }, [inputValue, state, commands]);
    // group commands by using `group` field
    const [groupedCommands, orderedCommands] = useMemo(() => {
        return groupCommands(filteredCommands, groupLabels);
    }, [filteredCommands, groupLabels]);
    const navigation = useListNavigation({ length: filteredCommands.length });
    const activeCommand = useMemo(() => {
        return filteredCommands.find((command) => command.index === navigation.activeIndex);
    }, [filteredCommands, navigation.activeIndex]);
    // TODO: add command options navigation
    // const activeCommandOptions = useMemo(() => {
    //   if (!activeCommand) return [];
    //   return activeCommand.options || [];
    // }, [activeCommand]);
    // const commandOptionsNavigation = useListNavigation({
    //   length: activeCommandOptions.length,
    // });
    const clearSuggester = useCallback(() => {
        setInputValue("");
    }, []);
    const selectCommand = useCallback((commandIndex, option) => {
        const command = orderedCommands[commandIndex];
        // navigation.set(0);
        return onRunCommand({
            command,
            inputValue: inputRef.current.value,
            inputElement: inputRef.current,
            setInputValue,
            navigation,
            option,
        });
    }, [navigation, inputRef, setInputValue, onRunCommand, orderedCommands]);
    const handleEnter = useCallback((index, option) => {
        selectCommand(index, option);
    }, [selectCommand]);
    const handleKeyEvent = useCallback((event) => {
        var _a;
        // If the user is navigating up/down we want to prevent the
        // default event if there is any command in the filteredCommands array
        if (isArrowKey(event.nativeEvent) &&
            ["ArrowUp", "ArrowDown"].includes(event.key) &&
            filteredCommands.length > 0) {
            event.preventDefault();
            event.stopPropagation();
        }
        // If the user is pressing backspace and the input is empty, we want to close the suggester.
        if (isBackspaceKey(event.nativeEvent) &&
            ((_a = event.target.value) === null || _a === void 0 ? void 0 : _a.length) === 0) {
            event.stopPropagation();
            event.preventDefault();
            return onClose();
        }
        // If the user is pressing escape, we want to close the suggester.
        // Sometimes this event is still caught by an above component, so we need to stop propagation.
        // If this is not working something else is catching the event.
        if (isEscKey(event.nativeEvent)) {
            event.stopPropagation();
            event.preventDefault();
            return onClose();
        }
        if (isEnterKey(event.nativeEvent) && !event.shiftKey) {
            event.stopPropagation();
            event.preventDefault();
            // If the user is not selecting a command, we just want to execute the prompt as is.
            if (filteredCommands.length === 0) {
                onPrompt(event.target.value);
                return clearSuggester();
            }
            // If the user is selecting a command, we want to execute the command action.
            if (navigation.activeIndex > -1) {
                return selectCommand(navigation.activeIndex);
            }
        }
        if (isArrowKey(event.nativeEvent)) {
            if (event.key === "ArrowUp") {
                return navigation.up();
            }
            if (event.key === "ArrowDown") {
                return navigation.down();
            }
            if (event.key === "ArrowLeft") {
                return navigateHistoryLeft();
            }
            if (event.key === "ArrowRight") {
                return navigateHistoryRight();
            }
        }
    }, [
        onPrompt,
        navigation,
        selectCommand,
        clearSuggester,
        onClose,
        filteredCommands.length,
        navigateHistoryLeft,
        navigateHistoryRight,
    ]);
    const handleOnChange = useCallback((event) => {
        setInputValue(event.target.value.trim());
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { role: "toolbar", className: "ai-popup" },
            !error && currentResult && state === AiPopupState.ContentGenerated && (React.createElement(InlineResultBox, { html: currentResult.html })),
            React.createElement("div", { className: "ai-popup__head" },
                React.createElement(Icon, { name: "stars", color: "currentColor", className: "ai-popup__icon" }),
                loading ? (React.createElement(AiLoader, null)) : (React.createElement(Textarea, { autoHeight: true, onChange: handleOnChange, className: "ai-popup__input", value: inputValue, ref: inputRef, autoFocus: true, placeholder: "Ask SmartSuite AI to write anything", [keyEvent]: handleKeyEvent })),
                !loading && results.length > 1 && (React.createElement("div", { className: "ai-popup__results" },
                    React.createElement(IconButton, { onClick: navigateHistoryLeft, ariaLabel: "Navigate results history left", name: "arrow-left", color: "currentColor" }),
                    React.createElement("span", null, currentResult.index + 1),
                    React.createElement("span", null, " of "),
                    React.createElement("span", null, results.length),
                    React.createElement(IconButton, { onClick: navigateHistoryRight, ariaLabel: "Navigate results history right", name: "arrow-right", color: "currentColor" })))),
            !loading &&
                !error &&
                [
                    AiPopupState.ContentGenerated,
                    AiPopupState.InlineContentGenerated,
                ].includes(state) && (React.createElement("div", { className: "ai-popup__warn" },
                React.createElement(Icon, { name: "warning", color: "currentColor" }),
                React.createElement(Text, { size: "s", color: TextColorNames.secondary }, "AI responses can be inaccurate or misleading."))),
            !loading && error && (React.createElement(React.Fragment, null, [
                AiPopupState.ContentGenerated,
                AiPopupState.InlineContentGenerated,
            ].includes(state) ? (React.createElement("div", { className: "ai-popup__error" },
                React.createElement(Icon, { name: "warning", color: "currentColor" }),
                React.createElement(Text, { size: "s", color: TextColorNames.secondary }, "AI request failed, please try again later."))) : (React.createElement("div", { className: "ai-popup__error" },
                React.createElement(Icon, { name: "warning", color: "currentColor" }),
                React.createElement(Text, { size: "s", color: TextColorNames.secondary }, error)))))),
        !error && filteredCommands.length > 0 && !loading && (React.createElement(AiActions, { popupRef: popupRef, commands: groupedCommands, activeIndex: navigation.activeIndex, setActiveIndex: navigation.set, onEnter: handleEnter, activeCommand: activeCommand, usage: React.createElement(AiUsage, { setupUrl: usageParams.setupUrl, limit: usageParams.callsLimit, current: usageParams.currentCount, isAdmin: usageParams.isAdmin, userHasOwnKey: usageParams.userHasOwnKey }) }))));
};
